import { Color3, Mesh, MeshBuilder, type Scene } from "@babylonjs/core";
import { GradientMaterial } from "@babylonjs/materials";

const SKY_DOME_DIAMETER = 2000;

/**
 * Sky dome. Art direction (see docs/Castaway Plan.md) calls for a soft,
 * near-flat pastel sky, not a dramatic HDR one — a physically-based
 * atmospheric-scattering sky (Babylon's SkyMaterial, tried first) is hard
 * to art-direct toward that predictably; a simple two-color vertical
 * gradient, unlit so it always reads as flat color regardless of scene
 * lighting, matches the reference far more reliably and is what's used
 * here instead. DayNightCycle drives the top/bottom colors directly.
 */
export class SkySystem {
  readonly mesh: Mesh;
  readonly material: GradientMaterial;

  constructor(scene: Scene) {
    this.mesh = MeshBuilder.CreateSphere("skyDome", { diameter: SKY_DOME_DIAMETER, segments: 16 }, scene);
    this.mesh.infiniteDistance = true;
    // The dome sits at a ~1000-unit radius, where scene EXP2 fog (added so
    // the ocean's far edge fades out) would reach ~100% density and paint
    // the whole sky flat fogColor, erasing the gradient. The dome *is* the
    // backdrop, so it's exempted rather than fogged.
    this.mesh.applyFog = false;
    // Not frozen via freezeWorldMatrix() (Phase 4 perf pass) unlike the
    // other static environment meshes — infiniteDistance re-translates
    // this mesh to stay centered on the camera every frame, which needs
    // its world matrix to keep recomputing normally.

    this.material = new GradientMaterial("skyMaterial", scene);
    this.material.disableLighting = true;
    this.material.backFaceCulling = false; // camera sits inside the dome
    // h = localY * scale + offset, then mix(bottom, top, h^smoothness) —
    // scale maps the sphere's radius to a 0..1 range so bottomColor sits at
    // the horizon (y=0) and topColor at the zenith (y=radius).
    this.material.scale = 1 / (SKY_DOME_DIAMETER / 2);
    this.material.offset = 0;
    this.material.smoothness = 1.1;
    this.mesh.material = this.material;
  }

  setGradientColors(topColor: Color3, bottomColor: Color3): void {
    this.material.topColor = topColor;
    this.material.bottomColor = bottomColor;
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
  }
}
